import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { staffAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Toaster } from '../../components/ui/sonner';
import { Briefcase, Loader2, LogOut, Search, StickyNote, CheckCircle2, Clock, PlayCircle, Save } from 'lucide-react';

const STATUS_META = {
    pending: { label: 'Pending', color: 'bg-neutral-100 text-neutral-700', icon: Clock },
    applied: { label: 'Applied', color: 'bg-blue-100 text-blue-700', icon: PlayCircle },
    in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-700', icon: PlayCircle },
    done: { label: 'Done', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
};

export default function StaffDashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [savingStatus, setSavingStatus] = useState(false);
    const [savingNotes, setSavingNotes] = useState(false);
    const [savingCredits, setSavingCredits] = useState(false);
    const [notesText, setNotesText] = useState('');
    const [sentInput, setSentInput] = useState(0);

    const fetchOrders = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 100 };
            if (search.trim()) params.search = search.trim();
            const res = await staffAPI.listOrders(params);
            let list = res.data?.orders || [];
            if (statusFilter !== 'all') {
                list = list.filter(o => (o.status || 'pending') === statusFilter);
            }
            setOrders(list);
            setTotal(res.data?.total || 0);
        } catch (err) {
            toast.error('Failed to load orders');
        } finally {
            setLoading(false);
        }
    }, [search, statusFilter]);

    useEffect(() => { fetchOrders(); }, [fetchOrders]);

    const handleLogout = async () => {
        await logout();
        navigate('/admin-login');
    };

    const openOrderDetail = (order) => {
        setSelectedOrder(order);
        setNotesText(order.staff_notes || '');
        setSentInput(order.applications_sent || 0);
    };

    const updateStatus = async (status) => {
        if (!selectedOrder) return;
        setSavingStatus(true);
        try {
            await staffAPI.updateStatus(selectedOrder.order_id, status);
            toast.success(`Status updated to ${STATUS_META[status]?.label || status}`);
            setSelectedOrder({ ...selectedOrder, status });
            setOrders((prev) => prev.map(o => o.order_id === selectedOrder.order_id ? { ...o, status } : o));
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to update status');
        } finally {
            setSavingStatus(false);
        }
    };

    const saveNotes = async () => {
        if (!selectedOrder) return;
        setSavingNotes(true);
        try {
            await staffAPI.updateNotes(selectedOrder.order_id, notesText);
            toast.success('Notes saved');
            setSelectedOrder({ ...selectedOrder, staff_notes: notesText, staff_notes_by: user?.name });
            setOrders((prev) => prev.map(o => o.order_id === selectedOrder.order_id ? { ...o, staff_notes: notesText } : o));
        } catch (err) {
            toast.error('Failed to save notes');
        } finally {
            setSavingNotes(false);
        }
    };

    const saveCredits = async () => {
        if (!selectedOrder) return;
        setSavingCredits(true);
        try {
            const res = await staffAPI.updateCredits(selectedOrder.order_id, parseInt(sentInput, 10) || 0);
            toast.success('Application count updated');
            const updated = { ...selectedOrder, ...res.data };
            setSelectedOrder(updated);
            setOrders((prev) => prev.map(o => o.order_id === selectedOrder.order_id ? { ...o, ...res.data } : o));
        } catch (err) {
            toast.error('Failed to update count');
        } finally {
            setSavingCredits(false);
        }
    };

    const stats = orders.reduce((acc, o) => {
        const s = o.status || 'pending';
        acc[s] = (acc[s] || 0) + 1;
        return acc;
    }, {});

    return (
        <div className="min-h-screen bg-neutral-50">
            <Toaster />
            {/* Header */}
            <header className="bg-white border-b border-neutral-200 sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary-600 to-violet-600 flex items-center justify-center">
                            <Briefcase className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <div className="text-sm font-semibold text-neutral-900">Staff Dashboard</div>
                            <div className="text-xs text-neutral-500">Multi-Apply Applications</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="hidden sm:block text-right">
                            <div className="text-sm font-medium text-neutral-900">{user?.name}</div>
                            <div className="text-xs text-neutral-500">{user?.email}</div>
                        </div>
                        <Button variant="outline" size="sm" onClick={handleLogout} data-testid="staff-logout-btn">
                            <LogOut className="w-4 h-4 mr-1" /> Logout
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
                {/* Stat cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {['pending', 'applied', 'in_progress', 'done'].map(s => {
                        const meta = STATUS_META[s];
                        const Icon = meta.icon;
                        return (
                            <div key={s} className="bg-white rounded-xl border border-neutral-200 p-4 flex items-center gap-3" data-testid={`stat-${s}`}>
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${meta.color}`}>
                                    <Icon className="w-5 h-5" />
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-neutral-900">{stats[s] || 0}</div>
                                    <div className="text-xs text-neutral-500">{meta.label}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle>Applications Queue ({total})</CardTitle>
                        <CardDescription>Update work status, add notes, and track application progress.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-col md:flex-row gap-3 mb-4">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                                <Input
                                    placeholder="Search by order ID..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && fetchOrders()}
                                    className="pl-9"
                                    data-testid="staff-search-input"
                                />
                            </div>
                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger className="md:w-48" data-testid="staff-status-filter">
                                    <SelectValue placeholder="Filter by status" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All statuses</SelectItem>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="applied">Applied</SelectItem>
                                    <SelectItem value="in_progress">In Progress</SelectItem>
                                    <SelectItem value="done">Done</SelectItem>
                                </SelectContent>
                            </Select>
                            <Button onClick={fetchOrders} data-testid="staff-refresh-btn">Refresh</Button>
                        </div>

                        {loading ? (
                            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary-600" /></div>
                        ) : (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Order</TableHead>
                                            <TableHead>User</TableHead>
                                            <TableHead>Plan</TableHead>
                                            <TableHead>Progress</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Notes</TableHead>
                                            <TableHead>Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {orders.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={7} className="text-center py-8 text-neutral-500" data-testid="staff-empty-msg">
                                                    No orders to work on right now.
                                                </TableCell>
                                            </TableRow>
                                        ) : orders.map((order) => {
                                            const status = order.status || 'pending';
                                            const meta = STATUS_META[status] || STATUS_META.pending;
                                            const total = order.applications_count || order.applications_total || 100;
                                            const sent = order.applications_sent || 0;
                                            const pct = total ? Math.min(100, Math.round((sent / total) * 100)) : 0;
                                            return (
                                                <TableRow key={order.order_id} data-testid={`order-row-${order.order_id}`}>
                                                    <TableCell className="font-mono text-xs">{order.order_id?.slice(0, 12)}...</TableCell>
                                                    <TableCell>
                                                        <div>
                                                            <div className="font-medium text-sm">{order.user_name || '—'}</div>
                                                            <div className="text-xs text-neutral-500">{order.user_email}</div>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell><Badge variant="outline">{order.package?.toUpperCase() || '—'}</Badge></TableCell>
                                                    <TableCell>
                                                        <div className="text-xs text-neutral-600 mb-1">{sent} / {total}</div>
                                                        <div className="w-24 h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                                                            <div className="h-full bg-primary-500" style={{ width: `${pct}%` }} />
                                                        </div>
                                                    </TableCell>
                                                    <TableCell><Badge className={meta.color}>{meta.label}</Badge></TableCell>
                                                    <TableCell>
                                                        {order.staff_notes ? <StickyNote className="w-4 h-4 text-amber-500" /> : <span className="text-neutral-300">—</span>}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Button size="sm" variant="outline" onClick={() => openOrderDetail(order)} data-testid={`open-order-${order.order_id}`}>
                                                            Manage
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </main>

            {/* Manage Order Dialog */}
            <Dialog open={!!selectedOrder} onOpenChange={(open) => !open && setSelectedOrder(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Manage Application</DialogTitle>
                        <DialogDescription>{selectedOrder?.order_id}</DialogDescription>
                    </DialogHeader>
                    {selectedOrder && (
                        <Tabs defaultValue="status" className="space-y-4">
                            <TabsList className="grid grid-cols-3">
                                <TabsTrigger value="status">Status</TabsTrigger>
                                <TabsTrigger value="progress">Progress</TabsTrigger>
                                <TabsTrigger value="notes">Notes</TabsTrigger>
                            </TabsList>

                            <TabsContent value="status" className="space-y-3">
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <div className="text-neutral-500 text-xs">Candidate</div>
                                        <div className="font-medium">{selectedOrder.user_name}</div>
                                        <div className="text-xs text-neutral-500">{selectedOrder.user_email}</div>
                                        {selectedOrder.user_phone && <div className="text-xs text-neutral-500">{selectedOrder.user_phone}</div>}
                                    </div>
                                    <div>
                                        <div className="text-neutral-500 text-xs">Plan</div>
                                        <div className="font-medium">{selectedOrder.package?.toUpperCase()}</div>
                                        <div className="text-xs text-neutral-500">{selectedOrder.applications_count || selectedOrder.applications_total || 100} apps</div>
                                    </div>
                                </div>
                                {selectedOrder.job_preferences && (
                                    <div className="p-3 bg-neutral-50 rounded-lg text-xs text-neutral-700 space-y-1">
                                        <div><strong>Roles:</strong> {selectedOrder.job_preferences.target_roles?.join(', ') || selectedOrder.job_preferences.job_titles?.join(', ') || 'Any'}</div>
                                        <div><strong>Locations:</strong> {selectedOrder.job_preferences.preferred_locations?.join(', ') || selectedOrder.job_preferences.cities?.join(', ') || 'Any'}</div>
                                    </div>
                                )}
                                <div>
                                    <Label className="text-sm">Update work status</Label>
                                    <div className="grid grid-cols-4 gap-2 mt-2">
                                        {Object.entries(STATUS_META).map(([key, meta]) => {
                                            const isCurrent = (selectedOrder.status || 'pending') === key;
                                            return (
                                                <Button
                                                    key={key}
                                                    variant={isCurrent ? 'default' : 'outline'}
                                                    size="sm"
                                                    onClick={() => updateStatus(key)}
                                                    disabled={savingStatus}
                                                    data-testid={`status-btn-${key}`}
                                                >
                                                    {meta.label}
                                                </Button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </TabsContent>

                            <TabsContent value="progress" className="space-y-3">
                                <Label className="text-sm">Applications submitted</Label>
                                <div className="flex gap-2 items-end">
                                    <Input
                                        type="number"
                                        min="0"
                                        value={sentInput}
                                        onChange={(e) => setSentInput(e.target.value)}
                                        data-testid="sent-input"
                                    />
                                    <span className="text-sm text-neutral-500 pb-2">/ {selectedOrder.applications_count || selectedOrder.applications_total || 100}</span>
                                    <Button onClick={saveCredits} disabled={savingCredits} data-testid="save-credits-btn">
                                        {savingCredits ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
                                    </Button>
                                </div>
                                <p className="text-xs text-neutral-500">Marking all applications as done will automatically set status to Done.</p>
                            </TabsContent>

                            <TabsContent value="notes" className="space-y-3">
                                <Label className="text-sm">Internal notes</Label>
                                <Textarea
                                    rows={6}
                                    value={notesText}
                                    onChange={(e) => setNotesText(e.target.value)}
                                    placeholder="Add context, blockers, or updates about this application batch..."
                                    data-testid="notes-textarea"
                                />
                                {selectedOrder.staff_notes_by && (
                                    <p className="text-xs text-neutral-500">Last edited by {selectedOrder.staff_notes_by}</p>
                                )}
                                <Button onClick={saveNotes} disabled={savingNotes} data-testid="save-notes-btn">
                                    {savingNotes ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save Notes
                                </Button>
                            </TabsContent>
                        </Tabs>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSelectedOrder(null)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
